import {
  Box,
  Button,
  DialogContent,
  FormControl,
  FormHelperText,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import Select from 'components/Select';
import { useTranslation } from 'react-i18next';
import Icon from 'components/Icon';
import CopyButton from 'components/CopyButton/CopyButton';
import { DialogActions } from 'components/Dialog';
import { useAppSelector } from 'store/hooks';
import { selectDefaultRole, selectRoles } from 'features/permissions/permissionsSlice';
import { notification } from 'features/notifications/toast/notification';
import CodeBox from 'components/CodeBox';
import { useInviteLink } from './useInviteLink';

const InviteUserSimple = () => {
  const { t } = useTranslation(['participants', 'modals']);

  const roles = useAppSelector(selectRoles);
  const defaultRole = useAppSelector(selectDefaultRole);

  const [selectedRole, setSelectedRole] = useState(defaultRole);
  const { inviteLink, error, isLoading } = useInviteLink(selectedRole);

  const handleRoleChange = async (event: SelectChangeEvent) => {
    setSelectedRole(event.target.value);
  };

  const handleEmailClick = () => {
    if (!inviteLink) {
      return;
    }

    const subject = encodeURIComponent(t('invite_participants.email_template.subject'));
    const emailBody = encodeURIComponent(
      t('invite_participants.email_template.body', { inviteLink })
    );

    const mailtoUrl = `mailto:?subject=${subject}&body=${emailBody}`;

    try {
      window.location.href = mailtoUrl;
    } catch (err) {
      notification.error(t('invite_participants.email_link_error'));
    }
  };

  const renderLink = () => {
    if (error) {
      return (
        <Typography component="span" color="error">
          {t('invite_participants.invite_link_error_placeholder')}
        </Typography>
      );
    }

    if (isLoading) {
      return (
        <Typography component="span" color="textDisabled">
          {t('invite_participants.invite_link_loading_placeholder')}
        </Typography>
      );
    }

    return <Typography component="span">{inviteLink}</Typography>;
  };

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            mb: 6,
          }}
        >
          <FormControl fullWidth>
            <Typography id="invite-role-select-label" gutterBottom>
              {t('invite_participants.role_select_label')}
            </Typography>
            <Select
              id="invite-role-select"
              labelId="invite-role-select-label"
              onChange={handleRoleChange}
              value={selectedRole}
              sx={{
                width: '100%',
              }}
            >
              {roles.map((role) => (
                <MenuItem key={role.name} value={role.name}>
                  {role.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box data-testid="code-box">
          <Typography gutterBottom>{t('invite_participants.invite_link_label')}</Typography>
          <CodeBox
            sx={{
              overflowX: 'auto',
              width: '100%',
              whiteSpace: 'nowrap',
            }}
          >
            {renderLink()}
          </CodeBox>
          <FormHelperText error={false}>
            {t('invite_participants.invite_link_help_text')}
          </FormHelperText>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<Icon name="envelope-mail" />} onClick={handleEmailClick}>
          {t('modals:invite_participants.btn_email_label')}
        </Button>
        <CopyButton key={selectedRole} text={inviteLink} autoFocus startIcon={<Icon name="copy" />}>
          {(isCopied) =>
            isCopied
              ? t('modals:invite_participants.btn_copied_label')
              : t('modals:invite_participants.btn_copy_label')
          }
        </CopyButton>
      </DialogActions>
    </>
  );
};

export default InviteUserSimple;
